/**
 * LES INDICATEURS — lot 103, 18/09/2026.
 *
 * Cadrage « Analyse financière : 7. les indicateurs » et « 8. les
 * arbitrages », maquette `maquette-analyse-indicateurs-v2.html`. Pur : la
 * liste des indicateurs, leur SENS, et la lecture d'une valeur face à un
 * objectif et à un seuil d'alerte.
 *
 * TROIS DÉCISIONS DU CADRAGE, tenues ici :
 *   - PÉRIODE CLOSE SEULEMENT : la dernière semaine complète, le dernier mois
 *     clos. Jamais la période en cours, qui paraîtrait toujours mauvaise ;
 *   - AUCUN OBJECTIF N'EST PROPOSÉ : l'écran démarre vide, et un indicateur
 *     sans objectif n'a pas de statut — il n'est ni bon ni mauvais, il est
 *     seulement mesuré ;
 *   - LE CA PAR SALARIÉ EST RETIRÉ : Stafa ne connaît aucun effectif
 *     (`chauffeurs` n'a que nom, note, actif). Vingt-trois indicateurs, pas
 *     vingt-quatre.
 */

export type Famille = 'finance' | 'facturation' | 'flotte' | 'commercial';
/** `haut` : plus haut est mieux. `bas` : plus bas est mieux. */
export type Sens = 'haut' | 'bas';
export type Rythme = 'semaine' | 'mois';
export type Unite = 'euros' | 'pourcent' | 'jours' | 'km' | 'nombre' | 'euro_km';

export interface Indicateur {
  readonly cle: string;
  readonly nom: string;
  readonly famille: Famille;
  readonly sens: Sens;
  readonly rythme: Rythme;
  readonly unite: Unite;
  /** Ce que l'indicateur mesure, et comment il est calculé. */
  readonly explication: string;
  /** Pour un RAPPEL : l'écran d'où vient le chiffre, et son calcul. */
  readonly ecran?: string;
}

export const FAMILLES: readonly { readonly cle: Famille; readonly nom: string }[] = [
  { cle: 'finance', nom: 'Finance' },
  { cle: 'facturation', nom: 'Facturation et recouvrement' },
  { cle: 'flotte', nom: 'Flotte' },
  { cle: 'commercial', nom: 'Commercial' },
];

export const INDICATEURS: readonly Indicateur[] = [
  // --- Finance, rappels des autres écrans -----------------------------------
  {
    cle: 'ca',
    nom: 'Chiffre d’affaires du mois',
    famille: 'finance',
    sens: 'haut',
    rythme: 'mois',
    unite: 'euros',
    explication: 'Comptes de produits de la balance du mois clos.',
    ecran: 'Résultat',
  },
  {
    cle: 'ebe_part',
    nom: 'EBE en part du chiffre d’affaires',
    famille: 'finance',
    sens: 'haut',
    rythme: 'mois',
    unite: 'pourcent',
    explication: 'EBE ÷ chiffre d’affaires du mois clos.',
    ecran: 'Résultat',
  },
  {
    cle: 'bfr_net',
    nom: 'BFR net d’affacturage',
    famille: 'finance',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'euros',
    explication:
      'Clients + non facturé − fournisseurs − affrètements − charges sociales − TVA − factures cédées, à la fin de la semaine.',
    ecran: 'BFR',
  },
  {
    cle: 'dso',
    nom: 'Délai d’encaissement réel (DSO)',
    famille: 'finance',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'jours',
    explication: 'Médiane de (règlement − date de pièce) sur les 91 derniers jours.',
    ecran: 'BFR',
  },
  {
    cle: 'dpo',
    nom: 'Délai de paiement réel (DPO)',
    famille: 'finance',
    sens: 'haut',
    rythme: 'semaine',
    unite: 'jours',
    explication: 'Même calcul que le DSO, côté fournisseurs.',
    ecran: 'BFR',
  },
  {
    cle: 'marge_km',
    nom: 'Marge réelle au kilomètre',
    famille: 'finance',
    sens: 'haut',
    rythme: 'mois',
    unite: 'euro_km',
    explication: 'Marge de la flotte ÷ kilomètres roulés du mois clos.',
    ecran: 'Rentabilité',
  },
  // --- Finance, propres ------------------------------------------------------
  {
    cle: 'fiabilite_previsions',
    nom: 'Fiabilité des prévisions',
    famille: 'finance',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication:
      'Écart moyen absolu entre le montant prévu et le montant réel des lignes rapprochées.',
  },
  {
    cle: 'poids_fixes',
    nom: 'Poids des charges fixes',
    famille: 'finance',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication: 'Loyers + salaires + structure ÷ chiffre d’affaires du mois clos.',
  },
  {
    cle: 'poids_loyers',
    nom: 'Poids des loyers de véhicules',
    famille: 'finance',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication: 'Loyers des véhicules ÷ chiffre d’affaires du mois clos.',
  },
  // --- Facturation et recouvrement -------------------------------------------
  {
    cle: 'delai_facturation',
    nom: 'Délai livraison → facture',
    famille: 'facturation',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'jours',
    explication:
      'Médiane, en jours calendaires, entre la dernière étape d’un ordre et l’émission de sa facture.',
  },
  {
    cle: 'montant_echu',
    nom: 'Montant échu',
    famille: 'facturation',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'euros',
    explication: 'Factures clients non réglées dont l’échéance est passée.',
  },
  {
    cle: 'paye_a_lecheance',
    nom: 'Payé à l’échéance',
    famille: 'facturation',
    sens: 'haut',
    rythme: 'mois',
    unite: 'pourcent',
    explication:
      'Part des factures réglées dans le mois dont le règlement est arrivé au plus tard à l’échéance.',
  },
  {
    cle: 'litiges_ouverts',
    nom: 'Litiges ouverts',
    famille: 'facturation',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'nombre',
    explication: 'Litiges non résolus à la fin de la semaine.',
  },
  {
    cle: 'avoirs',
    nom: 'Avoirs en part du chiffre d’affaires',
    famille: 'facturation',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication: 'Avoirs émis dans le mois ÷ factures émises dans le mois.',
  },
  // --- Flotte ----------------------------------------------------------------
  {
    cle: 'utilisation',
    nom: 'Taux d’utilisation de la flotte',
    famille: 'flotte',
    sens: 'haut',
    rythme: 'semaine',
    unite: 'pourcent',
    explication: 'Journées roulées ÷ (véhicules actifs × jours ouvrés de la semaine).',
  },
  {
    cle: 'km_vide',
    nom: 'Kilomètres à vide',
    famille: 'flotte',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'pourcent',
    explication: 'Kilomètres parcourus sans rien à bord ÷ kilomètres roulés.',
  },
  {
    cle: 'km_par_jour',
    nom: 'Kilomètres par jour roulé',
    famille: 'flotte',
    sens: 'haut',
    rythme: 'semaine',
    unite: 'km',
    explication: 'Kilomètres roulés ÷ journées roulées.',
  },
  {
    cle: 'immobilisation',
    nom: 'Jours d’immobilisation',
    famille: 'flotte',
    sens: 'bas',
    rythme: 'semaine',
    unite: 'jours',
    explication:
      'Jours d’indisponibilité des véhicules dans la semaine (entretien, panne, contrôle, carrosserie).',
  },
  // --- Commercial ------------------------------------------------------------
  {
    cle: 'poids_client',
    nom: 'Poids du premier client',
    famille: 'commercial',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication:
      'Part du plus gros client dans le chiffre d’affaires des 12 derniers mois.',
  },
  {
    cle: 'poids_trois_clients',
    nom: 'Poids des trois premiers clients',
    famille: 'commercial',
    sens: 'bas',
    rythme: 'mois',
    unite: 'pourcent',
    explication: 'Part des trois plus gros clients sur 12 mois glissants.',
  },
  {
    cle: 'part_affretement',
    nom: 'Part de l’affrètement',
    famille: 'commercial',
    // Tranché au doc 8 : plus haut = mieux.
    sens: 'haut',
    rythme: 'mois',
    unite: 'pourcent',
    explication:
      'Chiffre d’affaires des ordres sous-traités ÷ chiffre d’affaires du mois clos.',
  },
  {
    cle: 'marge_affretement',
    nom: 'Marge d’affrètement',
    famille: 'commercial',
    sens: 'haut',
    rythme: 'mois',
    unite: 'pourcent',
    explication: '(Vente − achat) ÷ vente, sur les ordres sous-traités du mois clos.',
  },
  {
    cle: 'prix_km',
    nom: 'Prix moyen facturé au kilomètre',
    famille: 'commercial',
    sens: 'haut',
    rythme: 'mois',
    unite: 'euro_km',
    explication: 'Chiffre d’affaires des ordres ÷ kilomètres roulés à bord.',
  },
];

export type Statut = 'vert' | 'orange' | 'rouge' | 'sans';

/**
 * LE STATUT D'UNE VALEUR. Sans objectif ni alerte : « sans » — un indicateur
 * que personne n'a cadré n'est ni bon ni mauvais (doc 8 : l'écran démarre sans
 * objectif).
 */
export function statutDe(
  valeur: number | null,
  objectif: number | null,
  alerte: number | null,
  sens: Sens,
): Statut {
  if (valeur === null || (objectif === null && alerte === null)) return 'sans';
  const atteint = (seuil: number) =>
    sens === 'haut' ? valeur >= seuil : valeur <= seuil;
  if (objectif !== null && atteint(objectif)) return 'vert';
  if (alerte !== null) return atteint(alerte) ? 'orange' : 'rouge';
  return 'orange';
}

/**
 * LA COHÉRENCE DES DEUX SEUILS, selon le sens. Rend la phrase du refus, ou
 * nul. Un seuil d'alerte plus exigeant que l'objectif rendrait l'écran
 * incompréhensible : tout serait rouge ou tout serait vert.
 */
export function coherenceDesSeuils(
  objectif: number | null,
  alerte: number | null,
  sens: Sens,
): string | null {
  if (objectif === null || alerte === null) return null;
  if (sens === 'haut' && alerte > objectif) {
    return 'plus haut est mieux : le seuil d’alerte doit être SOUS l’objectif';
  }
  if (sens === 'bas' && alerte < objectif) {
    return 'plus bas est mieux : le seuil d’alerte doit être AU-DESSUS de l’objectif';
  }
  return null;
}

export interface Tendance {
  /** L'écart relatif à la période précédente, ou nul. */
  readonly ecart: number | null;
  readonly sens: 'hausse' | 'baisse' | 'stable' | 'inconnue';
  /** Vrai quand la tendance va dans le bon sens pour cet indicateur. */
  readonly bonne: boolean | null;
}

export function tendanceDe(
  valeur: number | null,
  precedent: number | null,
  sens: Sens,
): Tendance {
  if (valeur === null || precedent === null || precedent === 0) {
    return { ecart: null, sens: 'inconnue', bonne: null };
  }
  const ecart = (valeur - precedent) / Math.abs(precedent);
  const mouvement = ecart > 0.001 ? 'hausse' : ecart < -0.001 ? 'baisse' : 'stable';
  return {
    ecart,
    sens: mouvement,
    bonne:
      mouvement === 'stable'
        ? null
        : sens === 'haut'
          ? mouvement === 'hausse'
          : mouvement === 'baisse',
  };
}

export interface Compteurs {
  readonly rouge: number;
  readonly orange: number;
  readonly vert: number;
  readonly sans: number;
}

export function compteurs(statuts: readonly Statut[]): Compteurs {
  return {
    rouge: statuts.filter((s) => s === 'rouge').length,
    orange: statuts.filter((s) => s === 'orange').length,
    vert: statuts.filter((s) => s === 'vert').length,
    sans: statuts.filter((s) => s === 'sans').length,
  };
}

/**
 * CE QUI EST UNE SOUS-LIGNE, et non une carte (maquette) : le nombre de
 * transports livrés non facturés sous le délai de facturation, la part échue
 * depuis plus de trente jours sous le montant échu, l'âge médian sous les
 * litiges ouverts. Vingt-trois cartes, pas vingt-six.
 */

/** L'indicateur d'une clé, ou nul — la liste fait foi. */
export function indicateurDe(cle: string): Indicateur | null {
  return INDICATEURS.find((i) => i.cle === cle) ?? null;
}
